/**
 * SimonCat Agent Platform - Agent API Client
 *
 * Backend endpoints:
 * GET    /api/agents          List (search/filter/sort)
 * GET    /api/agents/search   Full-text search
 * POST   /api/agents          Create agent
 * GET    /api/agents/:id      Details
 * PUT    /api/agents/:id      Update
 * DELETE /api/agents/:id      Delete
 */

import axios from 'axios';

const API_BASE = '/api';

export interface AgentDTO {
  id?: number;
  agentId: string;
  name: string;
  description?: string;
  type?: 'builtin' | 'custom';
  category?: string;
  model?: string;
  systemPrompt?: string;
  tools?: string;
  avatar?: string;
  status?: 'DRAFT' | 'ACTIVE' | 'INACTIVE' | 'ARCHIVED';
  createdBy?: number;
  createdAt?: string;
  updatedAt?: string;
}

export interface SearchResult<T> {
  items: T[];
  total: number;
  page: number;
  size: number;
  tookMs?: number;
}

export interface CreateAgentRequest {
  name: string;
  description?: string;
  type?: string;
  category?: string;
  model?: string;
  systemPrompt?: string;
  tools?: string;
  avatar?: string;
  status?: string;
}

export async function fetchAgents(params?: {
  query?: string;
  status?: string;
  model?: string;
  page?: number;
  size?: number;
  sortBy?: string;
  sortOrder?: string;
}): Promise<SearchResult<AgentDTO>> {
  const resp = await axios.get<SearchResult<AgentDTO>>(`${API_BASE}/agents`, { params });
  return resp.data;
}

export async function searchAgents(
  query: string,
  params?: { tags?: string; model?: string; page?: number; size?: number }
): Promise<SearchResult<AgentDTO>> {
  const resp = await axios.get<SearchResult<AgentDTO>>(`${API_BASE}/agents/search`, {
    params: { query, ...params },
  });
  return resp.data;
}

export async function fetchAgent(id: string): Promise<AgentDTO> {
  const resp = await axios.get<AgentDTO>(`${API_BASE}/agents/${id}`);
  return resp.data;
}

export async function createAgent(data: CreateAgentRequest): Promise<AgentDTO> {
  const resp = await axios.post<AgentDTO>(`${API_BASE}/agents`, data);
  return resp.data;
}

export async function updateAgent(id: string, data: Partial<AgentDTO>): Promise<AgentDTO> {
  const resp = await axios.put<AgentDTO>(`${API_BASE}/agents/${id}`, data);
  return resp.data;
}

export async function deleteAgent(id: string): Promise<boolean> {
  const resp = await axios.delete<{ success: boolean }>(`${API_BASE}/agents/${id}`);
  return resp.data.success;
}
